import { Button, DropdownItem, DropdownSection } from '@heroui/react';

import i18n from '~/common/helpers/i18n';

type OptionsSectionArgs = {
  options: { value: string; label: string }[];
  selectionMode: 'single' | 'multiple';
  selectionLimit?: number;
  dataTestId?: string;
};

const optionTestId = (dataTestId: string | undefined, value: string) =>
  dataTestId
    ? `${dataTestId}-menu-item-${value.toLowerCase().replaceAll(/\s+/g, '-')}`
    : undefined;

export const renderOptionsSection = ({
  options,
  selectionMode,
  selectionLimit,
  dataTestId
}: OptionsSectionArgs) => (
  <DropdownSection className='mb-0 max-h-[212px] overflow-y-auto pb-2 pr-1.5 pt-2 text-sm'>
    <>
      {selectionMode === 'multiple' && selectionLimit && (
        <DropdownItem
          key='header'
          className='mb-2 flex cursor-default items-center justify-between !bg-transparent leading-none tracking-tight !text-on-surface-variant'
          isReadOnly
          hideSelectedIcon
        >
          <span>
            {i18n.t('scenes.pipelines.labels.filter_limitation', {
              max: selectionLimit
            })}
          </span>
        </DropdownItem>
      )}
      {options.map(option => (
        <DropdownItem
          key={option.value}
          value={option.value}
          textValue={option.label}
          className='!text-label-lg text-on-surface-variant aria-checked:text-on-surface'
          data-testid={optionTestId(dataTestId, option.value)}
        >
          <span>{option.label}</span>
        </DropdownItem>
      ))}
    </>
  </DropdownSection>
);

type ActionsSectionArgs = {
  autoApply: boolean;
  onClear: () => void;
  onApply: () => void;
};

export const renderActionsSection = ({
  autoApply,
  onClear,
  onApply
}: ActionsSectionArgs) => (
  <DropdownSection className='mb-0 border-t-1 border-solid border-outline'>
    <DropdownItem
      key='actions'
      isReadOnly
      hideSelectedIcon
      className='mb-3 mt-3 cursor-default !bg-transparent p-0 hover:!bg-transparent'
    >
      <div className='flex items-center justify-between pr-4'>
        <Button
          variant='light'
          onPress={onClear}
          className='min-w-16 px-2.5 py-1.5 font-semibold leading-tight tracking-tight'
        >
          {i18n.t('scenes.pipelines.labels.filter_clear')}
        </Button>
        {!autoApply && (
          <Button
            variant='bordered'
            color='primary'
            onPress={onApply}
            className='min-w-16 border-1 px-2.5 py-1.5 font-semibold leading-tight tracking-tight'
          >
            {i18n.t('scenes.pipelines.labels.filter_apply')}
          </Button>
        )}
      </div>
    </DropdownItem>
  </DropdownSection>
);
